/* ------------------------ style ------------------------ */
import style from "./style.module.scss";

/* ------------------------ plugin ------------------------ */
import clsx from "clsx";
import React, { useRef, useState, useEffect } from "react";
import { useFullscreen } from 'ahooks';

/* ------------------------ type ------------------------ */
import type { PreviewVideoPropsType, VideoOperateType } from "../../index-type";

/* ------------------------ components ------------------------ */
import ResourceLoadError from "../ResourceLoadError";

/* ------------------------ assets ------------------------ */
import PauseIcon from "@/components/videoDom/icons/pauseIcon.svg";
import MuteIcon from "@/components/videoDom/icons/mute.svg";
import VolumeIcon from "@/components/videoDom/icons/volumeIcon.svg";
import StartPlayIcon from "@/components/videoDom/icons/start_playing.svg";
import VideoPlayBigIcon from "@/components/videoDom/icons/start_playing_big.svg";
import FullScreenIcon from "@/components/videoDom/icons/fullScreenIcon.svg";

const formatTime = (time: any) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(seconds).padStart(2, "0");
  return `${formattedMinutes}:${formattedSeconds}`;
};

const PreviewVideo: React.FC<PreviewVideoPropsType> = ({
  src,
  trackExtraParams = { previewVideo: {}, volumeBtn: {} },
  resourceOperateHandler,
  errorHandler
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const volumeNumRef = useRef<any>(0); // record the volume level
  const durationRef = useRef(0);

  const [volume, setVolume] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoPlayState, setVideoPlayState] = useState(false);
  const [isVolumeSlider, setIsVolumeSlider] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isVideoError, setIsVideoError] = useState(false);

  const [_, { enterFullscreen }] = useFullscreen(videoRef);

  useEffect(() => {
    const video = videoRef.current as HTMLVideoElement;
    const timeUpdate = () => { setCurrentTime(video.currentTime); };
    const loadedMetadata = () => { durationRef.current = video.duration; };
    const ended = () => { setVideoPlayState(false); };

    video.addEventListener("timeupdate", timeUpdate);
    video.addEventListener("loadedmetadata", loadedMetadata);
    video.addEventListener("ended", ended);

    return () => {
      video.removeEventListener("timeupdate", timeUpdate);
      video.removeEventListener("loadedmetadata", loadedMetadata);
      video.removeEventListener("ended", ended);
    };
  }, [src]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      if (document.fullscreenElement) {
        videoRef.current?.play();
      } else {
        videoRef.current?.pause();
        setVideoPlayState(false);
      }
    }

    document.addEventListener("fullscreenchange", handleFullScreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      videoRef.current?.pause();
      volumeNumRef.current = 0;
      setVideoPlayState(false);
      setCurrentTime(0);
      setVolume(0);
    };
  }, []);

  useEffect(() => {
    if (volume < 0.01 && videoRef.current) videoRef.current.volume = 0;
  }, [volume]);

  const handleVideoOperateCallback = (operateType: VideoOperateType) => {
    resourceOperateHandler(operateType, { volume });
  }

  const handleVideoError = () => {
    setIsVideoLoading(false);
    setIsVideoError(true);
    errorHandler();
  }

  const handleVideoTryAgain = () => {
    videoRef.current?.load();
    setIsVideoLoading(true);
    setIsVideoError(false);
    handleVideoOperateCallback('tryAgain');
  }

  const handleVideoPlayState = () => {
    videoPlayState ? videoRef.current?.pause() : videoRef.current?.play();
    setVideoPlayState(!videoPlayState);
    handleVideoOperateCallback(videoPlayState ? 'pause' : 'play');
  };

  const handlePlayProgress = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current!.currentTime) return;

    const rect = progressRef.current!.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    videoRef.current!.currentTime = pos * durationRef.current;
    videoRef.current!.play();
    setVideoPlayState(true);
  };

  const handleVolumeLevel = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = volumeRef.current!.getBoundingClientRect();
    const newVolume = 1 - (e.clientY - rect.top) / rect.height;
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    volumeNumRef.current = clampedVolume;
    videoRef.current!.volume = clampedVolume;
  };

  const handleVolumeState = () => {
    setIsVolumeSlider(false);

    if (volume) {
      setVolume(0);
    } else {
      const volumeNum = volumeNumRef.current ? volumeNumRef.current : 1;
      setVolume(volumeNum);
      videoRef.current && (videoRef.current.volume = volumeNum);
    }

    handleVideoOperateCallback(volume ? 'mute' : 'voiced');
  }

  const openFullScreen = () => {
    setIsVolumeSlider(false);
    setVideoPlayState(false);
    enterFullscreen();
  };

  return (
    <>
      <VideoPlayBigIcon
        className={clsx(style.video_play_big_icon, videoPlayState ? style.video_play_big_show : "")}
        onClick={handleVideoPlayState}
      />

      <div className={style.video_display_area}>
        <div className={style.video_rendering_box}>
          <ResourceLoadError
            isLoading={isVideoLoading}
            isError={isVideoError}
            {...(trackExtraParams?.tryAgainBtn ? { extraParams: trackExtraParams.tryAgainBtn } : {})}
            tryAgainHandler={handleVideoTryAgain}
          />

          <video
            {...(trackExtraParams.previewVideo)}
            className={style.video}
            ref={videoRef}
            src={src}
            muted={volume === 0 ? true : false}
            onClick={handleVideoPlayState}
            onLoadStart={() => setIsVideoLoading(true)}
            onLoadedData={() => setIsVideoLoading(false)}
            onError={handleVideoError}
          ></video>
        </div>

        <div className={style.video_controller_box}>
          <div>
            <div className={style.controller_icon} onClick={handleVideoPlayState}>
              {videoPlayState ? <PauseIcon /> : <StartPlayIcon />}
            </div>
          </div>

          <div className={style.video_progress_box}>
            <div className={style.duration}>{formatTime(currentTime)}</div>

            <div className={style.progress_bar_box}>
              <div
                ref={progressRef}
                className={style.progress_bar}
                onClick={handlePlayProgress}
              >
                <div
                  className={style.progress_length}
                  style={{ width: `${(currentTime / durationRef.current) * 100}%` }}
                />
              </div>

              <div className={style.current_time}>{formatTime(durationRef.current)}</div>
            </div>
          </div>

          <div className={style.right_operate_box}>
            <div className={clsx(style.controller_icon, style.volume_icon)}
              onMouseEnter={() => { !isVolumeSlider && setIsVolumeSlider(true) }}
              onMouseLeave={() => { isVolumeSlider && setIsVolumeSlider(false) }}
            >
              {isVolumeSlider && (
                <div className={style.volume_slider_box}
                  onMouseEnter={() => { setIsVolumeSlider(true) }}
                  onMouseLeave={() => { setIsVolumeSlider(false) }}
                >
                  <div
                    ref={volumeRef}
                    className={style.volume_slider}
                    onClick={handleVolumeLevel}
                  >
                    <div
                      className={style.volume_level}
                      style={{ height: `${volume * 85}%` }}
                    />
                  </div>
                </div>
              )}

              <div {...(trackExtraParams.volumeBtn)} onClick={handleVolumeState}>
                {volume < 0.01 ? <MuteIcon /> : <VolumeIcon />}
              </div>
            </div>

            <div className={style.controller_icon}>
              <FullScreenIcon onClick={openFullScreen} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PreviewVideo;